import { ISpendTotal } from '../services/emission/emission.model'

// Entered spend, as the API totals it: the money as entered, before any exchange rate or
// inflation adjustment, one figure per currency. Currencies are never added together, so a
// list with spend in two currencies shows two figures.

export const formatMoney = (amount: number, currencyCode: string): string => {
  const options = { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currencyCode, ...options }).format(amount)
  } catch {
    // An unknown or malformed code: show the number with the code rather than nothing.
    return `${new Intl.NumberFormat('en-GB', options).format(amount)} ${currencyCode}`.trim()
  }
}

/** One formatted figure per currency that carries a spend, largest first. */
export const formatSpendTotals = (totals?: ISpendTotal[] | null): string[] => {
  if (!Array.isArray(totals)) return []
  return totals
    .filter((total) => total && total.currencyCode && typeof total.amount === 'number' && !Number.isNaN(total.amount) && total.amount !== 0)
    .sort((a, b) => b.amount - a.amount)
    .map((total) => formatMoney(total.amount, total.currencyCode))
}

/**
 * Labelled figures for a totals strip: "Spend" when one currency, "Spend (GBP)" and "Spend (EUR)"
 * when the set carries more than one.
 */
export const spendTotalsAsStats = (totals?: ISpendTotal[] | null): { label: string; value: string }[] => {
  const withSpend = (Array.isArray(totals) ? totals : [])
    .filter((total) => total && total.currencyCode && typeof total.amount === 'number' && !Number.isNaN(total.amount) && total.amount !== 0)
    .sort((a, b) => b.amount - a.amount)
  const multi = withSpend.length > 1
  return withSpend.map((total) => ({
    label: multi ? `Spend (${total.currencyCode})` : 'Spend',
    value: formatMoney(total.amount, total.currencyCode),
  }))
}
